/**
 * @fileoverview SMS Alerts — view de configuração.
 *
 * Aesthetic: "Quiet Authority" — operational console. Coluna única,
 * hierarquia rigorosa, status como protagonista, chips editáveis pra
 * telefones, teste inline.
 *
 * A seção "Casos pendentes" (distribuição por janela de tempo) saiu em
 * 28/10/2026: os números vinham do conjunto sintético da tela, não do banco.
 *
 * Conecta com a infra real do sistema (PontaltechSMSProvider via 5
 * Settings keys + endpoint /sms-report/test). Não duplica nada.
 */

import React, { useCallback, useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Code,
  Info,
  Mail,
  Send,
  ShieldAlert,
  Smartphone,
  Trash2,
  X,
} from "lucide-react";

import {
  loadSmsConfig,
  saveSmsSetting,
  savePhones as savePhonesList,
  setEnabled as setSmsEnabled,
  sendTest as sendTestSms,
} from "./data/smsService";
import { formatPhoneDisplay, sanitizePastedPhone } from "../../utils/phoneInput";

// ── Tokens ────────────────────────────────────────────────────────────
const getTokens = (theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    isDark,
    pageBg: isDark ? "#0A0A0A" : "#F7F8FA",
    cardBg: isDark ? "rgba(14,16,22,0.92)" : "#ffffff",
    cardBgRaised: isDark ? "rgba(20,22,30,0.98)" : "#ffffff",
    cardBgInner: isDark ? "rgba(255,255,255,0.025)" : "rgba(15,23,42,0.025)",
    cardBorder: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.10)",
    cardBorderHover: isDark ? "rgba(124,154,255,0.30)" : "rgba(59,130,246,0.26)",
    text: isDark ? "#F2F4F8" : "#0F172A",
    textMuted: isDark ? "#94A3B8" : "#475569",
    textSubtle: isDark ? "#64748B" : "#94A3B8",
    accent: isDark ? "#7C9AFF" : "#3D5CFD",
    accentSoft: isDark ? "rgba(124,154,255,0.14)" : "rgba(61,92,253,0.10)",
    accentSoftStrong: isDark ? "rgba(124,154,255,0.24)" : "rgba(61,92,253,0.18)",
    success: isDark ? "#34D399" : "#059669",
    successSoft: isDark ? "rgba(52,211,153,0.14)" : "rgba(5,150,105,0.10)",
    danger: isDark ? "#F87171" : "#DC2626",
    dangerSoft: isDark ? "rgba(248,113,113,0.14)" : "rgba(220,38,38,0.10)",
    inputBg: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
  };
};

const useStyles = makeStyles((theme) => {
  const c = getTokens(theme);
  return {
    container: {
      maxWidth: 980,
      margin: "0 auto",
      paddingBottom: 60,
      fontFamily: `"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif`,
      color: c.text,
    },

    // ── Top bar ─────────────────────────────────────────────────────
    topBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    backBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      borderRadius: 8,
      border: `1px solid ${c.cardBorder}`,
      background: "transparent",
      color: c.textMuted,
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { borderColor: c.cardBorderHover, color: c.accent },
    },
    primaryBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "9px 16px",
      borderRadius: 8,
      border: `1px solid ${c.accent}`,
      background: c.accent,
      color: "#fff",
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { filter: "brightness(0.96)", transform: "translateY(-1px)" },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed", transform: "none" },
    },

    // ── Hero ────────────────────────────────────────────────────────
    hero: {
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 20,
      padding: "24px 28px",
      borderRadius: 16,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      marginBottom: 16,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 1,
        background: `linear-gradient(90deg, transparent, ${c.accentSoftStrong}, transparent)`,
      },
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", padding: "20px 16px" },
    },
    heroLeft: { display: "flex", flexDirection: "column", gap: 6 },
    heroTitleRow: { display: "flex", alignItems: "center", gap: 12 },
    heroIcon: {
      width: 42,
      height: 42,
      borderRadius: 12,
      background: `linear-gradient(135deg, ${c.accentSoftStrong}, ${c.accentSoft})`,
      color: c.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    heroTitle: {
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      lineHeight: 1.05,
    },
    heroSub: { fontSize: 13, color: c.textMuted, marginLeft: 54 },

    // Status pill (grande, visível à direita)
    statusPill: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "center",
      gap: 4,
    },
    statusButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 16px",
      borderRadius: 10,
      border: `1px solid ${c.cardBorder}`,
      background: c.cardBgInner,
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
    },
    statusButtonActive: {
      background: c.successSoft,
      color: c.success,
      borderColor: c.success,
      boxShadow: `0 0 0 4px ${c.successSoft}`,
    },
    statusButtonInactive: {
      background: c.cardBgInner,
      color: c.textSubtle,
      borderColor: c.cardBorder,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
    },
    statusHint: { fontSize: 11, color: c.textSubtle },

    // ── Section card ────────────────────────────────────────────────
    section: {
      padding: "20px 24px",
      borderRadius: 14,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      marginBottom: 14,
      transition: "border-color 0.15s ease",
      "&:hover": { borderColor: c.cardBorderHover },
    },
    sectionHead: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      marginBottom: 14,
      gap: 12,
    },
    sectionLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: c.textSubtle,
    },
    sectionHeadline: { fontSize: 15, fontWeight: 700, color: c.text },
    sectionMeta: { fontSize: 12, color: c.textMuted, marginTop: 4, lineHeight: 1.5 },

    // ── Rule grid (threshold + intervalo) ────────────────────────────
    ruleGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1.4fr",
      gap: 18,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    ruleField: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    ruleLabel: {
      fontSize: 11,
      fontWeight: 700,
      color: c.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    ruleInputRow: { display: "flex", gap: 8, alignItems: "center" },
    bigInput: {
      "& .MuiOutlinedInput-root": {
        background: c.inputBg,
        borderRadius: 10,
        height: 48,
        fontSize: 18,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: c.cardBorder },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: c.accent,
        borderWidth: 2,
      },
      "& input": { textAlign: "center", color: c.text },
    },
    unitSelect: {
      "& .MuiOutlinedInput-root": {
        background: c.inputBg,
        borderRadius: 10,
        height: 48,
        fontSize: 13,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: c.cardBorder },
      "& .MuiSelect-select": { color: c.text, padding: "12px 28px 12px 14px" },
      "& .MuiSelect-icon": { color: c.textMuted },
    },
    ruleHint: { fontSize: 11, color: c.textSubtle, marginTop: 4 },

    // ── Destinatários (chip input) ──────────────────────────────────
    chipInputWrap: {
      padding: "12px 12px",
      borderRadius: 10,
      background: c.inputBg,
      border: `1px solid ${c.cardBorder}`,
      transition: "border-color 0.15s ease",
      "&:focus-within": { borderColor: c.accent, boxShadow: `0 0 0 1px ${c.accentSoft}` },
    },
    chipList: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "center",
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 6px 6px 10px",
      borderRadius: 999,
      background: c.accentSoft,
      color: c.accent,
      fontSize: 12.5,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      border: `1px solid ${c.accentSoftStrong}`,
      animation: "$chipIn 0.18s ease",
    },
    chipRemove: {
      width: 18,
      height: 18,
      borderRadius: "50%",
      border: "none",
      background: "transparent",
      color: c.accent,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background 0.12s ease",
      "&:hover": { background: c.cardBgInner },
    },
    chipInput: {
      flex: 1,
      minWidth: 200,
      border: "none",
      background: "transparent",
      color: c.text,
      fontSize: 13,
      outline: "none",
      padding: "6px 4px",
      "&::placeholder": { color: c.textSubtle },
    },
    chipHint: { fontSize: 11, color: c.textSubtle, marginTop: 8 },

    // ── Test send ───────────────────────────────────────────────────
    testRow: {
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 10,
      alignItems: "center",
      marginTop: 14,
      paddingTop: 14,
      borderTop: `1px dashed ${c.cardBorder}`,
    },
    testInput: {
      "& .MuiOutlinedInput-root": {
        background: c.inputBg,
        borderRadius: 8,
        height: 42,
        fontSize: 13,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: c.cardBorder },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: c.accent },
      "& input": { color: c.text },
      "& input::placeholder": { color: c.textSubtle, opacity: 1 },
    },
    testResult: {
      marginTop: 12,
      padding: "10px 14px",
      borderRadius: 8,
      fontSize: 12.5,
      display: "flex",
      alignItems: "center",
      gap: 8,
      animation: "$slideIn 0.25s ease",
    },
    testResultSuccess: {
      background: c.successSoft,
      color: c.success,
      border: `1px solid ${c.successSoft}`,
    },
    testResultError: {
      background: c.dangerSoft,
      color: c.danger,
      border: `1px solid ${c.dangerSoft}`,
    },

    // ── Details (rodapé técnico) ────────────────────────────────────
    detailsBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      padding: "12px 16px",
      borderRadius: 10,
      border: `1px solid ${c.cardBorder}`,
      background: c.cardBg,
      color: c.textMuted,
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { borderColor: c.cardBorderHover, color: c.text },
    },
    detailsContent: {
      marginTop: 8,
      padding: "16px 20px",
      borderRadius: 10,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      fontSize: 12,
      color: c.textMuted,
      lineHeight: 1.7,
      "& code": {
        color: c.accent,
        background: c.accentSoft,
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 11,
        fontFamily: '"JetBrains Mono", "SF Mono", Menlo, monospace',
      },
      "& strong": { color: c.text },
    },
    detailsList: { listStyle: "none", padding: 0, margin: "8px 0 0" },
    detailsItem: {
      display: "flex",
      gap: 8,
      padding: "6px 0",
      borderBottom: `1px dashed ${c.cardBorder}`,
      "&:last-child": { borderBottom: "none" },
    },
    detailsItemKey: { fontWeight: 600, color: c.text, minWidth: 200 },

    // ── Loading / errors ────────────────────────────────────────────
    loadingState: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: 36,
      color: c.textMuted,
      fontSize: 13,
    },
    errorState: {
      padding: 14,
      borderRadius: 10,
      background: c.dangerSoft,
      color: c.danger,
      border: `1px solid ${c.dangerSoft}`,
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    emptyState: {
      padding: 32,
      textAlign: "center",
      color: c.textSubtle,
      fontSize: 13,
      lineHeight: 1.6,
    },

    // ── Animations ──────────────────────────────────────────────────
    "@keyframes chipIn": {
      from: { transform: "scale(0.85)", opacity: 0 },
      to: { transform: "scale(1)", opacity: 1 },
    },
    "@keyframes slideIn": {
      from: { transform: "translateY(-4px)", opacity: 0 },
      to: { transform: "translateY(0)", opacity: 1 },
    },
  };
});

// ── Component ─────────────────────────────────────────────────────────
const SmsAlertsView = ({ onBack }) => {
  const theme = useTheme();
  const colors = getTokens(theme);
  const classes = useStyles();

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [chipDraft, setChipDraft] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { kind: 'success'|'error', message }
  const [snackbar, setSnackbar] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // ── Carregar config ────────────────────────────────────────────────
  const reloadConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const cfg = await loadSmsConfig();
      setConfig(cfg);
    } catch (err) {
      setConfigError(err?.message || "Não foi possível carregar a configuração.");
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => { reloadConfig(); }, [reloadConfig]);

  // ── Mutations ──────────────────────────────────────────────────────
  const handleToggleEnabled = useCallback(async () => {
    if (!config) return;
    const next = !config.enabled;
    setConfig((p) => p ? { ...p, enabled: next } : p);
    setSaving(true);
    try {
      await setSmsEnabled(next);
      setSnackbar(next ? "SMS Alerts ativado." : "SMS Alerts desativado.");
    } catch (err) {
      setSnackbar(`Erro: ${err?.message || "—"}`);
      reloadConfig();
    } finally {
      setSaving(false);
    }
  }, [config, reloadConfig]);

  const handleSaveField = useCallback(async (key, value) => {
    setSaving(true);
    try {
      await saveSmsSetting(key, String(value));
      setSnackbar("Salvo.");
    } catch (err) {
      setSnackbar(`Erro: ${err?.message || "—"}`);
      reloadConfig();
    } finally {
      setSaving(false);
    }
  }, [reloadConfig]);

  const handleAddPhone = useCallback(async (raw) => {
    const sanitized = sanitizePastedPhone(raw);
    if (sanitized.length < 12) {
      setSnackbar("Número inválido — precisa ter pelo menos 12 dígitos (55 + DDD + número).");
      return;
    }
    if (!config) return;
    if (config.phones.includes(sanitized)) {
      setSnackbar("Esse número já está cadastrado.");
      return;
    }
    const next = [...config.phones, sanitized];
    setConfig((p) => p ? { ...p, phones: next, phonesFormatted: next.map(formatPhoneDisplay) } : p);
    setChipDraft("");
    setSaving(true);
    try {
      await savePhonesList(next);
      setSnackbar(`${formatPhoneDisplay(sanitized)} adicionado.`);
    } catch (err) {
      setSnackbar(`Erro: ${err?.message || "—"}`);
      reloadConfig();
    } finally {
      setSaving(false);
    }
  }, [config, reloadConfig]);

  const handleRemovePhone = useCallback(async (phone) => {
    if (!config) return;
    const next = config.phones.filter((p) => p !== phone);
    setConfig((p) => p ? { ...p, phones: next, phonesFormatted: next.map(formatPhoneDisplay) } : p);
    setSaving(true);
    try {
      await savePhonesList(next);
      setSnackbar("Número removido.");
    } catch (err) {
      setSnackbar(`Erro: ${err?.message || "—"}`);
      reloadConfig();
    } finally {
      setSaving(false);
    }
  }, [config, reloadConfig]);

  const handleSendTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await sendTestSms(testPhone || undefined);
      if (result?.success) {
        setTestResult({ kind: "success", message: `SMS de teste enviado para ${formatPhoneDisplay(result.phone) || result.phone}.` });
      } else {
        setTestResult({ kind: "error", message: `Falha: ${result?.error || "erro desconhecido"}` });
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.error || err?.message || "erro de rede";
      setTestResult({ kind: "error", message: `Falha: ${apiMsg}` });
    } finally {
      setTesting(false);
    }
  }, [testPhone]);

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className={classes.container}>
      {/* Top bar */}
      <div className={classes.topBar}>
        <button type="button" className={classes.backBtn} onClick={onBack}>
          <ArrowLeft size={14} /> Voltar para Multi unidades
        </button>
      </div>

      {/* Hero */}
      <div className={classes.hero}>
        <div className={classes.heroLeft}>
          <div className={classes.heroTitleRow}>
            <span className={classes.heroIcon}>
              <BellRing size={20} strokeWidth={2} />
            </span>
            <div>
              <div className={classes.heroTitle}>SMS Alerts</div>
            </div>
          </div>
          <div className={classes.heroSub}>
            Alertas automáticos por taxa de não-resposta. Quando a taxa
            ultrapassa o threshold por mais que o intervalo configurado,
            um SMS é enviado via Pontaltech para os telefones cadastrados.
          </div>
        </div>
        <div className={classes.statusPill}>
          {config && (
            <>
              <button
                type="button"
                className={`${classes.statusButton} ${config.enabled ? classes.statusButtonActive : classes.statusButtonInactive}`}
                onClick={handleToggleEnabled}
                disabled={saving || configLoading}
              >
                <span
                  className={classes.statusDot}
                  style={{ background: config.enabled ? colors.success : colors.textSubtle }}
                />
                {config.enabled ? "ATIVO" : "INATIVO"}
              </button>
              <span className={classes.statusHint}>
                {config.enabled ? "Disparando conforme regra abaixo" : "Disparo desabilitado"}
              </span>
            </>
          )}
          {configLoading && <CircularProgress size={18} />}
        </div>
      </div>

      {configError && (
        <div className={classes.errorState}>
          <ShieldAlert size={16} />
          {configError}
        </div>
      )}

      {/* REGRA DE DISPARO */}
      {!configLoading && !configError && config && (
        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <div>
              <div className={classes.sectionLabel}>01 · Regra de disparo</div>
              <div className={classes.sectionHeadline}>Quando disparar o alerta?</div>
              <div className={classes.sectionMeta}>
                A regra é validada pelo <code style={{ color: colors.accent, fontFamily: '"JetBrains Mono", monospace' }}>SMSReportSchedulerService</code> e
                executa periodicamente em background.
              </div>
            </div>
          </div>
          <div className={classes.ruleGrid}>
            <div className={classes.ruleField}>
              <span className={classes.ruleLabel}>Threshold (%)</span>
              <TextField
                type="number"
                variant="outlined"
                value={config.threshold}
                onChange={(e) => setConfig((p) => p ? { ...p, threshold: e.target.value } : p)}
                onBlur={(e) => handleSaveField("smsAlertThreshold", e.target.value)}
                className={classes.bigInput}
                inputProps={{ min: 0, max: 100 }}
              />
              <span className={classes.ruleHint}>
                Quando a taxa de não-resposta passar disso, o disparo é considerado.
              </span>
            </div>
            <div className={classes.ruleField}>
              <span className={classes.ruleLabel}>Intervalo entre envios</span>
              <div className={classes.ruleInputRow}>
                <TextField
                  type="number"
                  variant="outlined"
                  value={config.intervalValue}
                  onChange={(e) => setConfig((p) => p ? { ...p, intervalValue: e.target.value } : p)}
                  onBlur={(e) => handleSaveField("smsAlertIntervalValue", e.target.value)}
                  className={classes.bigInput}
                  style={{ width: 110 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  select
                  variant="outlined"
                  value={config.intervalUnit}
                  onChange={(e) => {
                    const val = e.target.value;
                    setConfig((p) => p ? { ...p, intervalUnit: val } : p);
                    handleSaveField("smsAlertIntervalUnit", val);
                  }}
                  className={classes.unitSelect}
                  style={{ flex: 1 }}
                >
                  <MenuItem value="minutes">minutos</MenuItem>
                  <MenuItem value="hours">horas</MenuItem>
                  <MenuItem value="days">dias</MenuItem>
                </TextField>
              </div>
              <span className={classes.ruleHint}>
                Após cada disparo, aguarda esse tempo antes de considerar disparar de novo.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* DESTINATÁRIOS */}
      {!configLoading && !configError && config && (
        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <div>
              <div className={classes.sectionLabel}>02 · Destinatários</div>
              <div className={classes.sectionHeadline}>
                Quem recebe o SMS · {config.phones.length} cadastrado(s)
              </div>
              <div className={classes.sectionMeta}>
                Digite o número e pressione <strong style={{ color: colors.text }}>Enter</strong> pra adicionar.
                Caracteres não-numéricos são removidos. DDI 55 é prefixado automaticamente se omitido.
              </div>
            </div>
          </div>

          <div className={classes.chipInputWrap}>
            <div className={classes.chipList}>
              {config.phonesFormatted.map((p, idx) => {
                const raw = config.phones[idx];
                return (
                  <span key={raw} className={classes.chip}>
                    <Smartphone size={11} />
                    {p}
                    <button
                      type="button"
                      className={classes.chipRemove}
                      onClick={() => handleRemovePhone(raw)}
                      disabled={saving}
                      title="Remover"
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
              <input
                type="tel"
                className={classes.chipInput}
                placeholder={config.phones.length ? "Adicionar outro número..." : "+55 (11) 99999-1111"}
                value={chipDraft}
                onChange={(e) => setChipDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    if (chipDraft.trim()) handleAddPhone(chipDraft);
                  } else if (e.key === "Backspace" && !chipDraft && config.phones.length) {
                    handleRemovePhone(config.phones[config.phones.length - 1]);
                  }
                }}
                disabled={saving}
              />
            </div>
          </div>
          {config.phones.length === 0 && (
            <div className={classes.chipHint}>
              <Info size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Sem destinatários — o disparo automático não terá efeito.
            </div>
          )}

          {/* Test send inline */}
          <div className={classes.testRow}>
            <TextField
              variant="outlined"
              placeholder={config.phones.length ? "Número opcional (usa o 1º da lista se vazio)" : "Informe um número pra testar"}
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className={classes.testInput}
            />
            <button
              type="button"
              className={classes.primaryBtn}
              disabled={testing || (!config.phones.length && !testPhone)}
              onClick={handleSendTest}
            >
              {testing ? <CircularProgress size={13} style={{ color: "#fff" }} /> : <Send size={13} />}
              {testing ? "Enviando..." : "Testar envio"}
            </button>
          </div>

          {testResult && (
            <div className={`${classes.testResult} ${testResult.kind === "success" ? classes.testResultSuccess : classes.testResultError}`}>
              {testResult.kind === "success" ? <CheckCircle2 size={14} /> : <ShieldAlert size={14} />}
              {testResult.message}
              <button
                type="button"
                onClick={() => setTestResult(null)}
                style={{ marginLeft: "auto", background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: 0, lineHeight: 0 }}
              >
                <X size={13} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Detalhes técnicos */}
      {!configLoading && !configError && config && (
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            className={classes.detailsBtn}
            onClick={() => setShowDetails((v) => !v)}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Code size={13} />
              Detalhes técnicos da integração
            </span>
            <span style={{ fontSize: 11, color: colors.textSubtle }}>
              {showDetails ? "ocultar" : "ver"}
            </span>
          </button>
          {showDetails && (
            <div className={classes.detailsContent}>
              Configuração persiste como linhas em <code>Setting</code> (uma por chave),
              consultadas pelo <code>SMSReportSchedulerService</code> em loop. Envio
              passa pelo <code>PontaltechSMSProvider</code>.
              <ul className={classes.detailsList}>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}><code>smsAlertEnabled</code></span>
                  <span>{config.enabled ? "enabled" : "disabled"}</span>
                </li>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}><code>smsAlertThreshold</code></span>
                  <span>{config.threshold}%</span>
                </li>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}><code>smsAlertIntervalValue</code></span>
                  <span>{config.intervalValue}</span>
                </li>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}><code>smsAlertIntervalUnit</code></span>
                  <span>{config.intervalUnit}</span>
                </li>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}><code>smsReportPhones</code></span>
                  <span>{config.phones.length} entradas (JSON array)</span>
                </li>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}>Endpoint de teste</span>
                  <span><code>POST /sms-report/test</code></span>
                </li>
                <li className={classes.detailsItem}>
                  <span className={classes.detailsItemKey}>Provider</span>
                  <span>Pontaltech SMS API</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2800}
        onClose={() => setSnackbar(null)}
        message={snackbar || ""}
      />
    </div>
  );
};

export default SmsAlertsView;
